import React, { useState, useEffect } from 'react';
import { getMenuConfig, updateRoleMenu, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { MENU, canAction } from '../menu';
import { orderSections } from '../kernelMenu';
import { KernelPage } from '../kernelAdminShared';

function MenuConfig({ user, onLogout }) {
  const { t } = useLanguage();
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState({}); // roleId -> Set(itemKeys)
  const [order, setOrder] = useState({}); // roleId -> [sectionKeys] nell'ordine scelto
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingRole, setSavingRole] = useState(null);

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMenuConfig();
      setRoles(data.roles || []);
      const next = {};
      const nextOrder = {};
      (data.roles || []).forEach((r) => {
        next[r.id] = new Set(r.menu || []);
        nextOrder[r.id] = orderSections(MENU, r.menu_order).map((section) => section.key);
      });
      setSelected(next);
      setOrder(nextOrder);
    } catch (err) {
      setError(errorMessage(err, t('menuConfig.errorLoad')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allKeys = MENU.flatMap((section) => section.items.map((item) => item.key));

  const toggle = (roleId, key) => {
    setSelected((prev) => {
      const set = new Set(prev[roleId] || []);
      if (set.has(key)) {
        set.delete(key);
      } else {
        set.add(key);
      }
      return { ...prev, [roleId]: set };
    });
  };

  const toggleAll = (roleId, keys, enabled) => {
    setSelected((prev) => {
      const set = new Set(prev[roleId] || []);
      keys.forEach((k) => {
        if (enabled) {
          set.add(k);
        } else {
          set.delete(k);
        }
      });
      return { ...prev, [roleId]: set };
    });
  };

  const defaultOrder = MENU.map((section) => section.key);

  const moveSection = (roleId, sectionKey, delta) => {
    setOrder((prev) => {
      const list = [...(prev[roleId] || defaultOrder)];
      const from = list.indexOf(sectionKey);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= list.length) return prev;
      [list[from], list[to]] = [list[to], list[from]];
      return { ...prev, [roleId]: list };
    });
  };

  const resetOrder = (roleId) => setOrder((prev) => ({ ...prev, [roleId]: defaultOrder }));

  const saveRole = async (role) => {
    setError('');
    setSuccess('');
    setSavingRole(role.id);
    try {
      // Solo le voci che esistono nel menu del modulo: eventuali chiavi vecchie rimaste nel database
      // (es. chiavi di sezione di versioni precedenti) verrebbero rifiutate dal server come non valide.
      const keys = allKeys.filter((key) => (selected[role.id] || new Set()).has(key)).sort();
      await updateRoleMenu(role.id, keys, order[role.id] || defaultOrder);
      setSuccess(t('menuConfig.saved', { name: role.name }));
    } catch (err) {
      setError(errorMessage(err, t('menuConfig.errorSave')));
    } finally {
      setSavingRole(null);
    }
  };

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('menuConfig.title')}</h1>
          <p className="page-subtitle">{t('menuConfig.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && <div className="loading">{t('common.loading')}</div>}

      {!loading &&
        roles.map((role) => {
          const roleSet = selected[role.id] || new Set();
          const allChecked = allKeys.every((k) => roleSet.has(k));
          const roleOrder = order[role.id] || defaultOrder;
          const sortedSections = roleOrder.map((key) => MENU.find((section) => section.key === key)).filter(Boolean);
          return (
            <div className="card" key={role.id}>
              <div className="card-header">
                <h3>{role.name}</h3>
                <div className="menu-config-actions">
                  <label className="form-row-check">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={(e) => toggleAll(role.id, allKeys, e.target.checked)}
                    />
                    <span>{t('menuConfig.toggleAll')}</span>
                  </label>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => resetOrder(role.id)}>
                    {t('menuConfig.resetOrder')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={savingRole === role.id}
                    onClick={() => saveRole(role)}
                  >
                    {savingRole === role.id ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
              <div className="card-body">
                <p className="page-subtitle">{t('menuConfig.orderHint')}</p>
                {sortedSections.map((section, position) => {
                  const sectionKeys = section.items.map((item) => item.key);
                  const sectionChecked = sectionKeys.every((k) => roleSet.has(k));
                  return (
                    <div className="menu-section" key={section.key}>
                      <div className="menu-section-header">
                        <strong>{t(section.labelKey)}</strong>
                        <span className="menu-order-controls">
                          <button
                            type="button"
                            className="menu-order-btn"
                            title={t('menuConfig.moveUp')}
                            aria-label={t('menuConfig.moveUp')}
                            disabled={position === 0}
                            onClick={() => moveSection(role.id, section.key, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="menu-order-btn"
                            title={t('menuConfig.moveDown')}
                            aria-label={t('menuConfig.moveDown')}
                            disabled={position === sortedSections.length - 1}
                            onClick={() => moveSection(role.id, section.key, 1)}
                          >
                            ▼
                          </button>
                        </span>
                        <label className="form-row-check">
                          <input
                            type="checkbox"
                            checked={sectionChecked}
                            onChange={(e) => toggleAll(role.id, sectionKeys, e.target.checked)}
                          />
                          <span>{t('menuConfig.toggleSection')}</span>
                        </label>
                      </div>
                      <div className="menu-items">
                        {section.items.map((item) => {
                          const checked = roleSet.has(item.key);
                          const missingAction = checked && !canAction({ actions: role.actions }, item.action);
                          return (
                            <label
                              className={`menu-item${checked ? ' is-checked' : ''}${missingAction ? ' would-disable' : ''}`}
                              key={item.key}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(role.id, item.key)}
                              />
                              <span className="menu-item-label">{t(item.labelKey)}</span>
                              <span className="menu-item-key">{item.key}</span>
                              {missingAction && (
                                <span
                                  className="warn-badge warn-badge-warning"
                                  title={t('menuConfig.wouldBeDisabled', { action: item.action })}
                                >
                                  !
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </KernelPage>
  );
}

export default MenuConfig;
